﻿enum ExtractType {
    static,
    year,
    month,
    day,
}
interface IExtractResult {
    /**
     * The extracted value
     */
    value: string | number,
    /**
     * New input value after extract
     */
    newInput: string;
}
interface IExtract {
    extractType: ExtractType;
    extract(input: string, logError: boolean): IExtractResult | null;
}

class StaticExtract implements IExtract {
    constructor(private pattern: string) {
    }

    extractType = ExtractType.static;

    extract(input: string, logError: boolean): IExtractResult | null {
        if (input.indexOf(this.pattern) === 0) {
            const newInputValue = input.substring(this.pattern.length);
            return {
                value: this.pattern,
                newInput: newInputValue
            };
        } else {
            if (logError) {
                console.error(`Failed to extract static value: ${input}, ${this.pattern}`);
            }
            return null;
        }
    }
}

class YearExtract implements IExtract {
    constructor(private patternLength: number) {
        if (this.patternLength !== 2 && this.patternLength !== 4) {
            throw new Error('Year extract only support yy or yyyy');
        }
    }
    extractType = ExtractType.year;
    extract(input: string, logError: boolean): IExtractResult | null {
        if (input.length >= this.patternLength) {
            let extractValue = input.substring(0, this.patternLength);
            if (this.patternLength === 2) {
                //we add prefix for two letter year
                extractValue = "20" + extractValue;
            }
            const yearNum = Number(extractValue);
            if (isNaN(yearNum) === false) {
                const newInputValue = input.substring(this.patternLength);
                return {
                    value: yearNum,
                    newInput: newInputValue
                };
            }
        }
        if (logError) {
            console.error(`Failed to extract year value: ${input}, ${this.patternLength}`);
        }
        return null;
    }
}

class MonthExtract implements IExtract {
    constructor(private patternLength: number) {
        if (this.patternLength > 2) {
            throw new Error('Month extract only support MM or M');
        }
    }
    extractType = ExtractType.month;
    extract(input: string, logError: boolean): IExtractResult | null {
        if (input.length >= this.patternLength) {
            let extractValue = input.substring(0, this.patternLength);
            let extractNum = Number(extractValue);
            if (isNaN(extractNum) === false && extractNum > 0 && extractNum <= 12) {
                if (this.patternLength === 1 && extractNum === 1) {
                    //When the pattern is M, the value maybe 10,11,12
                    const nextValue = input.substring(1, 2);
                    const nextNum = Number(nextValue);
                    if (isNaN(nextNum) === false && nextNum <= 2) {
                        extractValue += nextValue;
                        extractNum = Number(extractValue);
                    }
                }
                const newInputValue = input.substring(extractValue.length);
                return {
                    value: extractNum,
                    newInput: newInputValue
                };
            }
        }
        if (logError) {
            console.error(`Failed to extract month value: ${input}, ${this.patternLength}`);
        }
        return null;
    }
}

class DayExtract implements IExtract {
    constructor(private patternLength: number) {
        if (this.patternLength > 2) {
            throw new Error('Day extract only support dd or d');
        }
    }
    extractType = ExtractType.day;
    extract(input: string, logError: boolean): IExtractResult | null {
        if (input.length >= this.patternLength) {
            let extractValue = input.substring(0, this.patternLength);
            let extractNum = Number(extractValue);
            if (isNaN(extractNum) === false && extractNum > 0 && extractNum <= 31) {
                if (this.patternLength === 1 && extractNum <= 3) {
                    //When the pattern is d, the value maybe 1x,2x,3x
                    const nextValue = input.substring(1, 2);
                    const nextNum = Number(nextValue);
                    if (isNaN(nextNum) === false) {
                        const newExtractValue = extractValue + nextValue;
                        const newExtractNum = Number(newExtractValue);
                        if (newExtractNum <= 31) {
                            extractValue = newExtractValue;
                            extractNum = newExtractNum;
                        }
                    }
                }
                if (extractNum > 0 && extractNum <= 31) {
                    const newInputValue = input.substring(extractValue.length);
                    return {
                        value: extractNum,
                        newInput: newInputValue
                    };
                }
            }
        }
        if (logError) {
            console.error(`Failed to extract day value: ${input}, ${this.patternLength}`);
        }
        return null;
    }
}

export class DateExtractor {
    extracts: IExtract[] = [];
    hasYearExtract = false;
    hasMonthExtract = false;
    hasDayExtract = false;

    addExtract(pattern: string):void {
        if (pattern.indexOf('y') >= 0 && this.hasYearExtract === false) {
            const ext = new YearExtract(pattern.length);
            this.hasYearExtract = true;
            this.extracts.push(ext);
        }
        else if (pattern.indexOf('M') >= 0 && this.hasMonthExtract === false) {
            const ext = new MonthExtract(pattern.length);
            this.hasMonthExtract = true;
            this.extracts.push(ext);
        }
        else if (pattern.indexOf('d') >= 0 && this.hasDayExtract === false) {
            const ext = new DayExtract(pattern.length);
            this.hasDayExtract = true;
            this.extracts.push(ext);
        } else {
            const ext = new StaticExtract(pattern);
            this.extracts.push(ext);
        }
    }

    convertToDate(input: string, logError: boolean): Date | null {
        let newInput = input;
        let yearNum = 0;
        let monthNum = 0;
        let dayNum = 0;
        for (let i = 0; i < this.extracts.length; i++) {
            const ext = this.extracts[i];
            const result = ext.extract(newInput, logError);
            if (result == null) {
                if (logError) {
                    console.error(`Failed to convert date ${input}`);
                }
                return null;
            }
            newInput = result.newInput;
            if (ext.extractType === ExtractType.year) {
                yearNum = Number(result.value);
            } else if (ext.extractType === ExtractType.month) {
                monthNum = Number(result.value);
            } if (ext.extractType === ExtractType.day) {
                dayNum = Number(result.value);
            }
        }

        if (yearNum > 0 || monthNum > 0 || dayNum > 0) {
            const date = new Date(yearNum, monthNum - 1, dayNum);
            if (isNaN(date.getTime()) === false && date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum) {
                return date;
            }
        }
        if (logError) {
            console.error(`Failed to convert date ${input}, ${yearNum}-${monthNum}-${dayNum}`);
        }
        return null;
    }
}

export function convertToDateExtractor(format: string): DateExtractor | null {
    const extractor: DateExtractor = new DateExtractor();
    let currentPattern = '';
    for (let i = 0; i < format.length; i++) {
        if (i === 0) {
            currentPattern = format[i];
        } else {
            if (format[i] === format[i - 1]) {
                currentPattern += format[i];
            } else {
                //add extract and start a new pattern
                extractor.addExtract(currentPattern);

                currentPattern = format[i];
            }
        }

        if (i === format.length - 1) {
            //the last pattern
            extractor.addExtract(currentPattern);
        }
    }

    if (extractor.hasYearExtract === false || extractor.hasMonthExtract === false || extractor.hasDayExtract === false) {
        console.error(`Invalid format for date extractor: ${format}`);
        return null;
    }

    return extractor;
}